import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';

import ApplicationContext from '@op/shared/src/models/how/application-context';
import { IErrorActivity } from '@op/shared/src/hubs/user-activity-hub';
import { AuthenticationService } from '@op/shared/src/services';
import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

let isUnloading = false;

if (typeof window !== 'undefined') {
  const onUnload = () => {
    isUnloading = true;
  };
  window.addEventListener('beforeunload', onUnload);
  window.addEventListener('unload', onUnload);
  window.addEventListener('pagehide', onUnload);

  // Centralized Fix: Intercept and suppress transient DOM race conditions
  // (like removeChild) at the window level before they trigger popups.
  window.addEventListener(
    'error',
    (event) => {
      if (isUnloading || isTeardownError(event.error || event)) {
        // Prevent the error from showing up in console or triggering industrial-strength crash handlers.
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true, // Use capture to catch it as early as possible
  );
}

function isTeardownError(error: any) {
  if (!error) return false;
  // Patterns common in React teardown/unmount race conditions during page transitions
  const message = String(error.message || '').toLowerCase();
  const name = String(error.name || '').toLowerCase();
  const stack = String(error.stack || error.reason?.stack || '').toLowerCase();

  return (
    message.includes('removechild') ||
    message.includes('insertbefore') ||
    message.includes('node not found') ||
    message.includes('not a child of this node') ||
    name.includes('notfounderror') ||
    name.includes('hierarchyrequesterror') ||
    stack.includes('removechildfromcontainer') ||
    stack.includes('commitdeletion')
  );
}

export default class ErrorBoundary extends React.Component<Props, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // If the error occurs during page unload or matches teardown patterns,
    // we skip logging it to the activity hub to avoid noise from normal page transitions.
    if (isUnloading || isTeardownError(error)) {
      return;
    }

    const crashInfo: IErrorActivity = {
      message: error.message,
      stack: error.stack || '',
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // TODO: remove commented lines
    // console.log('❌ APP CRASH -> ', crashInfo);

    // try {
    //   localStorage.setItem('LAST_CRASH_LOG', JSON.stringify(crashInfo));
    // } catch (e) {
    //   console.error('Failed to save crash log to sessionStorage', e);
    // }

    try {
      // send data to error activity hub
      if (ApplicationContext.userActivityHub) {
        ApplicationContext.userActivityHub.logError(crashInfo);
      }
    } catch {
      // never throw from error boundary
    }
  }

  /**
   * Refreshes the application.
   * If the application is running in an embedded context (e.g., within an iframe on a host platform),
   * we attempt to reload the top-level window (window.top).
   *
   * Rationale:
   * In an embedded setup, configuration parameters like apiKey and authToken are often provided
   * via a loader script in the host HTML. If the widget itself crashes, a simple iframe reload
   * might lose these initialization parameters. Reloading the top-level window ensures that
   * the host's loader re-runs and correctly re-initializes the widget.
   */
  refresh = () => {
    try {
      if (
        ApplicationContext &&
        ApplicationContext.configuration &&
        ApplicationContext.configuration.isEmbeddingPlatform &&
        window.self !== window.top
      ) {
        window.top.location.reload();
        return;
      }
    } catch (e) {}
    window.location.reload();
  };

  resetApp = async () => {
    // 1. Call logout API to invalidate session/SSO cookies on the backend before clearing client storage
    try {
      await AuthenticationService.instance.logOut();
    } catch (e) {
      console.error('Failed to call logout API during app reset', e);
    }

    // 2. Clear Service Worker caches
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      } catch (e) {
        console.error('Failed to clear caches', e);
      }
    }

    // 3. Clear Cookies
    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });

    // 4. Clear Storage
    localStorage.clear();
    sessionStorage.clear();

    // 5. Set skip_sso so router doesn't trigger auto SSO loop on login page
    sessionStorage.setItem('skip_sso', 'true');

    // 6. Navigate to login route
    window.location.replace('/login');
  };

  render() {
    if (this.state.hasError) {
      // CENTRALIZED FIX: If the error is a known teardown race condition or occurs during unloading,
      // return null to silently allow the browser redirect/reload to complete.
      if (isUnloading || isTeardownError(this.state.error)) {
        return null;
      }

      return (
        <Dialog open fullWidth maxWidth="sm">
          <DialogTitle>Something went wrong</DialogTitle>

          <DialogContent>
            There was an error loading the application.
            <br />
            Our developers have been notified.
          </DialogContent>

          <DialogActions sx={{ padding: 3 }}>
            <Grid container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <Button variant="contained" onClick={this.refresh} autoFocus>
                  Refresh
                </Button>
              </Grid>
              {!ApplicationContext?.configuration?.isEmbeddingPlatform && (
                <Grid item>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: (theme) => theme.palette.error.main,
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.error.dark,
                      },
                    }}
                    onClick={this.resetApp}>
                    Reset
                  </Button>
                </Grid>
              )}
            </Grid>
          </DialogActions>
        </Dialog>
      );
    }

    return this.props.children;
  }
}
